import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  FormControl, FormControlLabel, FormLabel, IconButton, LinearProgress, Radio, RadioGroup,
  Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useWorkspace } from '../WorkspaceContext.js';
import ScenarioCreateDialog from './ScenarioCreateDialog.js';
import { rainPresetLabel } from './rainPresets.js';

const STATUS_LABELS = {
  created: 'noch nicht berechnet',
  queued: 'in der Warteschlange',
  computing: 'wird berechnet',
  done: 'berechnet',
  error: 'Fehler bei der Berechnung',
};

const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>{children}</Typography>
);

const ScenariosPanel = () => {
  const {
    scenarios,
    rainPresets,
    activeScenarioId,
    activeScenario,
    setActiveScenarioId,
    createScenario,
    duplicateScenario,
    deleteScenario,
    saveScenarioInformation,
    updateComputationMethod,
    updateAreaExtensionMethod,
    startComputation,
    isAdmin,
  } = useWorkspace();

  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState(null);

  // The edit form is a local draft of the active scenario so typing does not
  // write to the backend on every keystroke. It is seeded when the active
  // scenario changes and deliberately not when the scenario object itself is
  // replaced -- progress polling hands out a fresh object every five seconds,
  // which would otherwise wipe out whatever the user is typing.
  const activeScenarioRef = useRef(activeScenario);
  activeScenarioRef.current = activeScenario;

  useEffect(() => {
    const scenario = activeScenarioRef.current;
    if (!scenario) {
      setForm(null);
      return;
    }
    setForm({
      name: scenario.name,
      selectedScenario: scenario.selected_scenario || 'custom',
      rainAmount: scenario.rain_amount,
      rainDuration: scenario.rain_duration,
      minimumTimeStep: scenario.minimum_time_step,
    });
  }, [activeScenarioId]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const running = activeScenario
    && (activeScenario.status === 'queued' || activeScenario.status === 'computing');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {scenarios.length === 0 && (
        <Alert severity="info">Für diese Region gibt es noch kein Szenario.</Alert>
      )}

      {scenarios.map((scenario) => (
        <Box
          key={scenario.id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            border: '1px solid', borderColor: scenario.id === activeScenarioId ? '#1976d2' : '#e0e0e0',
            borderRadius: '8px', px: 1, py: 0.75,
            backgroundColor: scenario.id === activeScenarioId ? '#f1f8fd' : 'transparent',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
              {scenario.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {STATUS_LABELS[scenario.status] || scenario.status}
              {scenario.status === 'computing' && scenario.progress != null
                ? ` — ${scenario.progress}%`
                : ''}
            </Typography>
          </Box>
          <Tooltip title="Szenario duplizieren">
            <IconButton size="small" onClick={() => duplicateScenario(scenario.id)}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Szenario löschen">
            <IconButton size="small" color="error" onClick={() => setPendingDelete(scenario)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Switch
            checked={scenario.id === activeScenarioId}
            onChange={(e) => setActiveScenarioId(e.target.checked ? scenario.id : null)}
          />
        </Box>
      ))}

      <Button
        variant="contained"
        startIcon={<AddIcon />}
        sx={{ mt: 1 }}
        onClick={() => setCreateOpen(true)}
      >
        Szenario hinzufügen
      </Button>

      {form && activeScenario && (
        <>
          <Divider sx={{ mt: 2 }} />
          <SectionTitle>Szenario bearbeiten</SectionTitle>

          <TextField
            label="Name des Szenarios"
            variant="outlined"
            size="small"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => updateForm({ name: e.target.value })}
          />

          <RadioGroup
            value={form.selectedScenario}
            onChange={(e) => updateForm({ selectedScenario: e.target.value })}
          >
            <FormControlLabel value="light" control={<Radio size="small" />} label={rainPresetLabel('light', rainPresets)} />
            <FormControlLabel value="heavy" control={<Radio size="small" />} label={rainPresetLabel('heavy', rainPresets)} />
            <FormControlLabel value="custom" control={<Radio size="small" />} label="Eigene Einstellungen" />
          </RadioGroup>

          {form.selectedScenario === 'custom' && (
            <>
              <TextField
                label="Regenmenge [mm/h]"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                value={form.rainAmount ?? ''}
                onChange={(e) => updateForm({ rainAmount: e.target.value })}
              />
              <TextField
                label="Regendauer [s]"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                value={form.rainDuration ?? ''}
                onChange={(e) => updateForm({ rainDuration: e.target.value })}
              />
            </>
          )}

          {/* The solver's internal time resolution is an expert setting and is
              only offered to administrators. */}
          {isAdmin && (
            <TextField
              label="Minimale Zeitauflösung [s]"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              value={form.minimumTimeStep ?? ''}
              onChange={(e) => updateForm({ minimumTimeStep: e.target.value })}
            />
          )}

          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={() => saveScenarioInformation(activeScenario.id, form)}
          >
            Änderungen speichern
          </Button>

          <Divider sx={{ mt: 2 }} />
          <SectionTitle>Berechnung</SectionTitle>

          <FormControl component="fieldset" sx={{ mt: 0.5 }}>
            <FormLabel component="legend" sx={{ fontSize: '13px' }}>Berechnungsmethode</FormLabel>
            <RadioGroup
              value={activeScenario.computation_method || 'shallow_water_ai'}
              onChange={(e) => updateComputationMethod(activeScenario.id, e.target.value)}
            >
              <FormControlLabel
                value="shallow_water_ai"
                control={<Radio size="small" />}
                label="Flachwassergleichungen mit KI (schnell mit KI)"
              />
              <FormControlLabel
                value="shallow_water"
                control={<Radio size="small" />}
                label="Flachwassergleichungen numerisch (langsam ohne KI)"
              />
              <FormControlLabel
                value="fast_heuristic"
                control={<Radio size="small" />}
                label="Schnelle Heuristik (HAND + Mulden)"
              />
              {/* The neural network itself, rather than the KI-labelled method
                  above, which currently runs the heuristic. Admins only -- but
                  still rendered (disabled) for everyone else when a scenario is
                  already set to it, so the group is not left with nothing
                  selected. */}
              {(isAdmin || activeScenario.computation_method === 'pinn') && (
                <FormControlLabel
                  value="pinn"
                  disabled={!isAdmin}
                  control={<Radio size="small" />}
                  label="Neuronales Netz (echte KI, experimentell)"
                />
              )}
            </RadioGroup>
            {/* Like the minimum time step, the flat area extension is an expert
                setting and is only offered to administrators. */}
            {isAdmin && (activeScenario.computation_method === 'shallow_water_ai'
              || activeScenario.computation_method === 'fast_heuristic'
              || activeScenario.computation_method === 'pinn') && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={activeScenario.area_extension_method === 'fixed_1km'}
                    onChange={(e) => updateAreaExtensionMethod(
                      activeScenario.id,
                      e.target.checked ? 'fixed_1km' : 'automatic'
                    )}
                  />
                }
                label="Gebiet pauschal um 1 km erweitern (schneller, statt automatischer Einzugsgebietsberechnung)"
              />
            )}
          </FormControl>

          {running && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {activeScenario.status === 'computing'
                  ? `Wird berechnet — ${activeScenario.progress ?? 0} %`
                  : 'In der Warteschlange…'}
              </Typography>
              {activeScenario.status === 'computing'
                ? <LinearProgress variant="determinate" value={activeScenario.progress ?? 0} />
                : <LinearProgress />}
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrowIcon />}
            sx={{ mt: 1 }}
            disabled={running}
            onClick={() => startComputation(activeScenario.id)}
          >
            Gefahrenkarte berechnen
          </Button>
        </>
      )}

      <ScenarioCreateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={createScenario}
        scenarios={scenarios}
        rainPresets={rainPresets}
      />

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Szenario wirklich löschen?</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie das Szenario „{pendingDelete ? pendingDelete.name : ''}" löschen
          wollen?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              deleteScenario(pendingDelete.id);
              setPendingDelete(null);
            }}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScenariosPanel;
